import { useEffect, useState } from 'react';
import { Title, useGetIdentity, usePermissions } from 'react-admin';
import { Link as RouterLink } from 'react-router-dom';
import {
  Card, CardContent, Typography, Grid, Stack, Skeleton, Box, Button, Chip, Avatar,
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import EventIcon from '@mui/icons-material/Event';
import MailIcon from '@mui/icons-material/MarkEmailUnread';
import SchoolIcon from '@mui/icons-material/School';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import HistoryIcon from '@mui/icons-material/History';

import { getToken } from './providers/authProvider.js';
import { tokens } from './theme.js';

const API = import.meta.env.VITE_API_URL || 'http://localhost:4000/api';

// ─────────────────────────────────────────────────────────────
// Welcome banner — full-bleed cream card with serif greeting
// ─────────────────────────────────────────────────────────────
const greeting = () => {
  const h = new Date().getHours();
  if (h < 5) return 'Working late';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  if (h < 21) return 'Good evening';
  return 'Working late';
};

const Welcome = ({ identity }) => {
  const firstName = identity?.fullName?.split(' ')[0] || 'there';
  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${tokens.cream2} 0%, ${tokens.cream} 60%, ${tokens.cream3} 100%)`,
        border: `1px solid ${tokens.line}`,
        borderLeft: `4px solid ${tokens.gold}`,
        borderRadius: 1.5,
        p: { xs: 3, sm: 4 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ md: 'center' }} justifyContent="space-between">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 56, height: 56,
              background: tokens.green800,
              color: tokens.cream,
              border: `2px solid ${tokens.gold}`,
              boxShadow: `0 0 0 4px ${tokens.cream}`,
            }}
          >
            <SchoolIcon />
          </Avatar>
          <Box>
            <Typography
              sx={{
                fontFamily: tokens.serif,
                fontWeight: 500,
                fontSize: { xs: '1.5rem', sm: '1.85rem' },
                color: tokens.ink,
                lineHeight: 1.1,
              }}
            >
              {greeting()}, <em style={{ color: tokens.green700 }}>{firstName}</em>.
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: tokens.gold700,
                fontSize: 11,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                fontWeight: 600,
                display: 'block',
                mt: 0.75,
              }}
            >
              Maple Leaf · Content Management
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            component="a"
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewIcon />}
            variant="outlined"
            color="primary"
            size="small"
          >
            View site
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────
// Quick actions — pill row of common tasks
// ─────────────────────────────────────────────────────────────
const QUICK_ACTIONS = [
  { to: '/news/create',   icon: <ArticleIcon fontSize="small" />,     label: 'Add news' },
  { to: '/events/create', icon: <EventIcon fontSize="small" />,       label: 'Add event' },
  { to: '/achievements/create', icon: <EmojiEventsIcon fontSize="small" />, label: 'Add achievement' },
  { to: '/gallery-images/create', icon: <PhotoLibraryIcon fontSize="small" />, label: 'Upload photo' },
];

const QuickActions = () => (
  <Box>
    <Typography
      variant="overline"
      sx={{ color: tokens.ink3, display: 'block', mb: 1 }}
    >
      Quick actions
    </Typography>
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {QUICK_ACTIONS.map((a) => (
        <Button
          key={a.to}
          component={RouterLink}
          to={a.to}
          startIcon={a.icon}
          variant="outlined"
          size="small"
          sx={{
            borderColor: tokens.line,
            color: tokens.ink2,
            background: tokens.paper,
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': { borderColor: tokens.gold, background: tokens.cream2, color: tokens.green800 },
          }}
        >
          {a.label}
        </Button>
      ))}
    </Stack>
  </Box>
);

// ─────────────────────────────────────────────────────────────
// Stat card — clickable, branded, with attention indicator
// ─────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, loading, to, accent, attention }) => (
  <Card
    component={to ? RouterLink : 'div'}
    to={to}
    sx={{
      display: 'block',
      textDecoration: 'none',
      color: 'inherit',
      height: '100%',
      borderTop: `3px solid ${accent}`,
      transition: 'transform .15s ease, box-shadow .15s ease',
      cursor: to ? 'pointer' : 'default',
      '&:hover': to ? { transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(14,42,31,0.08)' } : {},
    }}
  >
    <CardContent sx={{ p: 2.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography
            variant="overline"
            sx={{ color: tokens.ink3, fontSize: 10, lineHeight: 1.2 }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontFamily: tokens.serif,
              fontWeight: 500,
              fontSize: '2.4rem',
              lineHeight: 1.1,
              color: tokens.ink,
              mt: 1,
            }}
          >
            {loading ? <Skeleton width={60} /> : value}
          </Typography>
        </Box>
        <Box
          sx={{
            color: accent,
            background: `${accent}14`,
            borderRadius: '50%',
            width: 38, height: 38,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
      </Stack>
      {attention && !loading && value > 0 && (
        <Chip
          label="Needs review"
          size="small"
          sx={{
            mt: 1.5,
            background: tokens.goldPale,
            color: tokens.gold700,
            fontSize: 10,
            height: 20,
            fontWeight: 700,
            letterSpacing: '0.06em',
          }}
        />
      )}
    </CardContent>
  </Card>
);

// ─────────────────────────────────────────────────────────────
// Activity card
// ─────────────────────────────────────────────────────────────
const ActivityCard = ({ items }) => {
  if (!items?.length) return null;
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <HistoryIcon sx={{ color: tokens.green700, fontSize: 20 }} />
          <Typography variant="overline" sx={{ color: tokens.ink3 }}>
            Recent activity
          </Typography>
        </Stack>
        <Stack divider={<Box sx={{ borderTop: `1px solid ${tokens.line2}` }} />} spacing={0}>
          {items.slice(0, 8).map((entry) => (
            <Box key={entry.id} sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ color: tokens.ink2, fontSize: 13 }}>
                <strong style={{ color: tokens.green800 }}>{entry.action}</strong>
                {entry.resourceType && (
                  <> · {entry.resourceType}{entry.resourceId ? ` #${entry.resourceId}` : ''}</>
                )}
              </Typography>
              <Typography variant="caption" sx={{ color: tokens.ink4 }}>
                {new Date(entry.createdAt).toLocaleString()}
              </Typography>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────
// Main Dashboard
// ─────────────────────────────────────────────────────────────
export const Dashboard = () => {
  const { identity } = useGetIdentity();
  const { permissions } = usePermissions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    fetch(`${API}/admin/dashboard/summary`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : r.json().then((e) => Promise.reject(e)))
      .then(setData)
      .catch((e) => setError(e?.error?.message || 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  const counts = data?.counts || {};

  return (
    <>
      <Title title="MLIS Admin" />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Welcome identity={identity} />

        <QuickActions />

        {error && (
          <Typography variant="body2" sx={{ color: '#b54033' }}>{error}</Typography>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              icon={<ArticleIcon fontSize="small" />}
              label="Published news"
              value={counts.publishedNews ?? 0}
              loading={loading}
              to="/news"
              accent={tokens.green700}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              icon={<EventIcon fontSize="small" />}
              label="Upcoming events"
              value={counts.upcomingEvents ?? 0}
              loading={loading}
              to="/events"
              accent={tokens.gold700}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              icon={<SchoolIcon fontSize="small" />}
              label="New enquiries"
              value={counts.newEnquiries ?? 0}
              loading={loading}
              to="/admission-enquiries"
              accent={tokens.gold}
              attention
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              icon={<MailIcon fontSize="small" />}
              label="Unread messages"
              value={counts.unreadContacts ?? 0}
              loading={loading}
              to="/contact-submissions"
              accent={tokens.greenSoft}
              attention
            />
          </Grid>
        </Grid>

        {permissions === 'super_admin' && (
          <ActivityCard items={data?.recentActivity || []} />
        )}
      </Box>
    </>
  );
};
